import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Form, Button, Row, Col } from 'react-bootstrap';
import './AdvancedSearch.css';
import { getWriters, getAdvancedSearch } from "../../api";
import { useLanguage } from '../../i18n/LanguageContext';

const AdvancedSearch = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [writers, setWriters] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchParams, setSearchParams] = useState({
    author: '',
    selectedAuthor: '',
    title: '',
    content: '',
    keywords: [],
    timeRange: '',
    school: '',
    type: '',
    topic: '',
    selectedTopic: ''
  });

  // Anahtar kelime input'u için ayrı state
  const [keywordInput, setKeywordInput] = useState('');

  useEffect(() => {
    const fetchWriters = async () => {
      try {
        const response = await getWriters();
        if (response && response.data) {
          setWriters(response.data);
        }
      } catch (error) {
        console.error('Yazarlar yüklenirken hata oluştu:', error);
      }
    };

    fetchWriters();
  }, []);

  // Örnek veriler (gerçek verilerle değiştirilecek)
  const topics = [
    'Eğitim',
    'Tarih',
    'Kültür',
    'Sanat',
    'Edebiyat',
    'Bilim',
    'Teknoloji'
  ];

  const timeRanges = [
    '1920-1930',
    '1931-1940',
    '1941-1950',
    '1951-1960',
    '1961-1970',
    '1971-1980'
  ];

  const schools = [
    'İstanbul Üniversitesi',
    'Ankara Üniversitesi',
    'Ege Üniversitesi',
    'Marmara Üniversitesi',
    'Hacettepe Üniversitesi',
    'Boğaziçi Üniversitesi'
  ];

  const types = [
    'Makale',
    'Tez',
    'Bildiri',
    'Kitap',
    'Dergi',
    'Rapor'
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSearchParams(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Anahtar kelime input'u için özel handler
  const handleKeywordInputChange = (e) => {
    setKeywordInput(e.target.value);
  };

  // Enter veya virgül ile anahtar kelime ekleme
  const handleKeywordKeyPress = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addKeyword();
    }
  };

  // Anahtar kelime ekleme
  const addKeyword = () => {
    const keyword = keywordInput.trim();
    if (keyword && !searchParams.keywords.includes(keyword)) {
      setSearchParams(prev => ({
        ...prev,
        keywords: [...prev.keywords, keyword]
      }));
      setKeywordInput('');
    }
  };

  // Anahtar kelime silme
  const removeKeyword = (keywordToRemove) => {
    setSearchParams(prev => ({
      ...prev,
      keywords: prev.keywords.filter(keyword => keyword !== keywordToRemove)
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // console.log('🚀 Form submit edildi');
    // console.log('📝 Ham searchParams:', searchParams);
    
    // Boş olmayan parametreleri filtrele
    const validParams = Object.entries(searchParams)
      .filter(([key, value]) => {
        if (key === 'keywords') {
          return value.length > 0; // keywords array'i boş değilse
        }
        return value.trim() !== ''; // diğer alanlar için
      })
      .reduce((acc, [key, value]) => {
        acc[key] = value;
        return acc;
      }, {});

    // console.log('✅ Filtrelenmiş validParams:', validParams);
    
    if (Object.keys(validParams).length === 0) {
      console.warn('⚠️ Hiç arama parametresi girilmedi!');
      alert(t('advancedSearchPage.alertMinCriteria'));
      return;
    }

    // API çağrısı yap
    performAdvancedSearch(validParams);
  };

  const performAdvancedSearch = async (validParams) => {
    try {
      setIsLoading(true);
      
      // console.log('🔍 Gelişmiş Arama Parametreleri:', validParams);
      // console.log('📡 API çağrısı yapılıyor...');
      
      // Backend'in beklediği formata uygun parametre mapping
      const apiParams = { ...validParams };
      
      // keywords -> keyword (backend'de keyword bekleniyor)
      if (apiParams.keywords) {
        apiParams.keyword = apiParams.keywords.join(',');
        delete apiParams.keywords;
      }
      
      // console.log('🔄 API için dönüştürülmüş parametreler:', apiParams);
      
      const response = await getAdvancedSearch(apiParams);
      
      // console.log('✅ API Response alındı:', response);
      // console.log('📊 Response data:', response.data || response);
      // console.log('📈 Sonuç sayısı:', (response.data || response)?.length || 'Bilinmiyor');
      
      // Sonuçları URL parametreleri olarak geçir
      const searchQuery = new URLSearchParams(validParams).toString();
      
      // SearchResults sayfasına yönlendir ve sonuçları state olarak geçir
      navigate(`/aramaSonuclari?${searchQuery}&advanced=true`, {
        state: { searchResults: response.data || response, searchParams: validParams }
      });
    } catch (error) {
      console.error('❌ Gelişmiş arama sırasında hata oluştu:', error);
      console.error('🔍 Hata detayları:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      // Hata durumunda da yönlendir (backend hazır olmadığında)
      const searchQuery = new URLSearchParams(validParams).toString();
      navigate(`/aramaSonuclari?${searchQuery}&advanced=true`);
    } finally {
      setIsLoading(false);
      console.log('🏁 Arama işlemi tamamlandı');
    }
  };

  return (
    <div className="gelismisArama-container">
      <h1 className="search-title">{t('nav.advancedSearch')}</h1>
      <p className="search-description">{t('advancedSearchPage.intro')}</p>

      <form onSubmit={handleSubmit} className="search-form">
        <div className="form-row">
          {/* <div className="form-group">
            <label>Yazar</label>
            <input
              type="text"
              name="author"
              value={searchParams.author}
              onChange={handleInputChange}
              placeholder="Yazar adı giriniz"
              className="form-input"
            />
          </div> */}
          <div className="form-group">
            <label>{t('advancedSearchPage.authorNames')}</label>
            <select
              name="selectedAuthor"
              value={searchParams.selectedAuthor}
              onChange={handleInputChange}
              className="form-select"
            >
              <option value="">{t('advancedSearchPage.selectOption')}</option>
              {writers.map((writer) => (
                <option key={writer.id} value={writer.id}>{writer.name} {writer.surname}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-group">
          <label>{t('advancedSearchPage.titleField')}</label>
          <input
            type="text"
            name="title"
            value={searchParams.title}
            onChange={handleInputChange}
            placeholder={t('advancedSearchPage.titlePlaceholder')}
            className="form-input"
          />
        </div>

        <div className="form-group">
          <label>{t('advancedSearchPage.contentField')}</label>
          <input
            type="text"
            name="content"
            value={searchParams.content}
            onChange={handleInputChange}
            placeholder={t('advancedSearchPage.contentPlaceholder')}
            className="form-input"
          />
        </div>
        <div className="form-group">
          <label>{t('articleDetail.keywordsTitle')}</label>
          <input
            type="text"
            name="keywordInput"
            value={keywordInput}
            onChange={handleKeywordInputChange}
            onKeyPress={handleKeywordKeyPress}
            onBlur={addKeyword}
            placeholder={t('advancedSearchPage.keywordsPlaceholder')}
            className="form-input"
          />
          
          {/* Anahtar kelime tag'ları */}
          {searchParams.keywords.length > 0 && (
            <div className="keywords-container" style={{ marginTop: '10px' }}>
              {searchParams.keywords.map((keyword, index) => (
                <span key={index} className="keyword-tag">
                  {keyword}
                  <button
                    type="button"
                    onClick={() => removeKeyword(keyword)}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
        {/* <div className="form-row">
          <div className="form-group">
            <label>Zaman Çizelgesi</label>
            <select
              name="timeRange"
              value={searchParams.timeRange}
              onChange={handleInputChange}
              className="form-select"
            >
              <option value="">Zaman</option>
              {timeRanges.map((range, index) => (
                <option key={index} value={range}>{range}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Okul</label>
            <select
              name="school"
              value={searchParams.school}
              onChange={handleInputChange}
              className="form-select"
            >
              <option value="">Okul</option>
              {schools.map((school, index) => (
                <option key={index} value={school}>{school}</option>
              ))}
            </select>
          </div>
        </div> */}

        {/* <div className="form-row">
          <div className="form-group">
            <label>Biçim</label>
            <select
              name="type"
              value={searchParams.type}
              onChange={handleInputChange}
              className="form-select"
            >
              <option value="">Tip</option>
              {types.map((type, index) => (
                <option key={index} value={type}>{type}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Tip</label>
            <select
              name="selectedTopic"
              value={searchParams.selectedTopic}
              onChange={handleInputChange}
              className="form-select"
            >
              <option value="">Seçiniz</option>
              {topics.map((topic, index) => (
                <option key={index} value={topic}>{topic}</option>
              ))}
            </select>
          </div>
        </div> */}

        {/* <div className="form-group">
          <label>Konum</label>
          <input
            type="text"
            name="topic"
            value={searchParams.topic}
            onChange={handleInputChange}
            placeholder="Konum giriniz"
            className="form-input"
          />
        </div> */}

        <div className="search-info">
          
            <button type="submit" className="advanced-search-button" disabled={isLoading}>
            {isLoading ? t('advancedSearchPage.searching') : t('advancedSearchPage.submit')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdvancedSearch; 